import { useContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ListItem from "@mui/material/ListItem";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import SoftBox from "components/SoftBox";
import { TextField } from "../StyledComponents";
import AutocompletePopperComponent from "../AutocompletePopperComponent";
import { getObjectPaths } from "helpers/common";

import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import { combineStyles } from "helpers/common";

const styles = {
  w100: {
    width: "100%"
  },
  listItemMargin_t16: {
    marginTop: "16px"
  },
  asterisk: {
    color: "#ff667c"
  }
};

const Autocomplete = styled(MuiAutocomplete)(() => ({
  "& .MuiAutocomplete-endAdornment": {
    display: "none"
  }
}));

const TagListItem = ({ action, fieldValue, allowEditWorkflow }) => {
  const { actionForm, formErrors } = useContext(ComponentPropsContext);
  const { setActionForm, setFormErrors, filterValidationErrors } =
    useContext(ComponentPropsContextApi);
  const [open, setOpen] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [invalidEmailInput, setInvalidEmailInput] = useState(false);
  const { context } = useSelector((store) => store?.designer?.editorContext);
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const isActiveFlow = currentSavedFlow?.shared_status === "active";

  const handleInputChange = (e, value) => {
    if (value) {
      handleFormErrors(action.field, value);
    }
    e;

    if (value && value[0] === "@") {
      setOpen(true);
    }

    setInvalidEmailInput(false);
  };

  const closeAutocomplete = () => {
    setOpen(false);
  };

  const handleAutocompleteChange = (e, value) => {
    const isEmailValid = validateInput(value[value.length - 1]);

    if (!isEmailValid && value.length > 0) {
      setInvalidEmailInput(true);
      return;
    }

    const newValue = value.map((v) => ({ value: v, isVariable: v[0] === "@" }));

    setActionForm({
      ...actionForm,
      [action.field]: {
        value: newValue
      }
    });

    filterValidationErrors(action.field);
    closeAutocomplete();
  };

  const filterAutompleteOptions = (options, params) => {
    const filtered = options?.filter((option) => {
      return option.toLowerCase().includes(params.inputValue?.toLowerCase());
    });

    if (!filtered.length) {
      closeAutocomplete();
    }
    return filtered;
  };

  const setAutocompleteDataSource = () => {
    const stepsPaths = getObjectPaths(context.steps);
    const triggersPaths = getObjectPaths(context.triggers);
    const settingsPaths = getObjectPaths(context.settings);
    const flowConfigurationPaths = getObjectPaths(context.flow_configuration);
    const sharedFlowConfigurationPaths = getObjectPaths(context.shared_flow_configurations);
    const varOptions = getVariablesDS();
    let options = [];

    stepsPaths.forEach((path) => {
      const pathString = `@steps.${path.join(".")}`;
      options.push(pathString);
    });

    triggersPaths.forEach((path) => {
      const pathString = `@triggers.${path.join(".")}`;
      options.push(pathString);
    });

    settingsPaths.forEach((path) => {
      const pathString = `@settings.${path.join(".")}`;
      options.push(pathString);
    });

    flowConfigurationPaths.forEach((path) => {
      options.push(`@flow_configuration.${path.join(".")}`);
    });

    sharedFlowConfigurationPaths.forEach((path) => {
      options.push(`@shared_flow_configurations.${path.join(".")}`);
    });

    const idPath = `@flow_id`;
    const tenantIdPath = `@tenant_id`;
    const integrationIdsPath = `@integration_ids`;

    options = [idPath, tenantIdPath, integrationIdsPath, ...varOptions, ...options];

    setDataSource(options);
  };

  const getVariablesDS = () => {
    const varOptions = [];

    try {
      const varKeys = context?.variables;
      Object.keys(varKeys).map((key) => {
        if (varKeys[key]) {
          if (typeof varKeys[key] === "object") {
            Object.keys(varKeys[key]).map((varKey) => {
              varOptions.push(`@variables.${key}.${varKey}`);
            });
          } else {
            varOptions.push(`@variables.${key}`);
          }
        } else {
          varOptions.push(`@variables.${key}`);
        }
      });
    } catch (e) {
      console.log("error", e);
    }

    return varOptions;
  };

  const handleFormErrors = (field, value) => {
    if ((!value || !value.length) && action.isRequired) {
      setFormErrors((prev) => (prev.includes(field) ? prev : [...prev, field]));
    } else {
      filterValidationErrors(field);
    }
  };

  const validateInput = (email) => {
    if (email && email[0] === "@") return true;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleTextFieldBlur = (e) => {
    const { value } = e.target;
    handleFormErrors(action.field, actionForm[action.field]?.value);

    if (value) {
      const isEmailValid = validateInput(value);
      if (!isEmailValid) {
        setInvalidEmailInput(true);
      } else {
        setInvalidEmailInput(false);
        handleFormErrors(action.field, value);

        // Create a enter event to trigger the autocomplete
        const enterEvent = new KeyboardEvent("keydown", {
          bubbles: true, // Allows the event to bubble up through the DOM
          key: "Enter", // The key value
          code: "Enter" // The physical key on the keyboard
        });
        // Dispatch the event
        e.target.dispatchEvent(enterEvent);
      }
    } else {
      setInvalidEmailInput(false);
    }
  };

  useEffect(() => {
    setAutocompleteDataSource();
  }, [context]);

  return (
    <ListItem sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
      <SoftBox width="100%">
        <Autocomplete
          open={open}
          freeSolo
          multiple
          options={dataSource}
          value={fieldValue && Array.isArray(fieldValue) ? fieldValue?.map((v) => v.value) : []}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option}>
                {option}
              </li>
            );
          }}
          renderValue={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                sx={{ bgcolor: "#7443FF", color: "#fff" }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={
                invalidEmailInput
                  ? "Invalid email address"
                  : formErrors.includes(action.field)
                  ? `${action.label} is required.`
                  : ""
              }
              error={formErrors?.includes(action.field) || invalidEmailInput}
              variant="outlined"
              label={
                <span
                  style={{
                    color:
                      formErrors?.includes(action.field) || invalidEmailInput
                        ? "#ff667c"
                        : "inherit"
                  }}
                >
                  {action.label}
                  {action.isRequired && (
                    <Box component="span" sx={styles.asterisk}>
                      *
                    </Box>
                  )}
                </span>
              }
              onBlur={handleTextFieldBlur}
              disabled={isActiveFlow || !allowEditWorkflow}
            />
          )}
          filterOptions={filterAutompleteOptions}
          onInputChange={handleInputChange}
          onChange={handleAutocompleteChange}
          slotProps={{
            popper: {
              component: AutocompletePopperComponent
            }
          }}
          selectOnFocus
          handleHomeEndKeys
          disabled={isActiveFlow || !allowEditWorkflow}
        />
      </SoftBox>
    </ListItem>
  );
};

export default TagListItem;
